import { useMemo, useState } from 'react';

import { SORT_KEY_TO_BACKEND } from '@/sections/asset/config/columns';
import { isWebApplication } from '@/sections/asset/utils/assetTypeDetection';

// Re-export for components that import from this file
export { isWebApplication };
import { keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useDebounce } from 'use-debounce';

import { DateRangeFilter } from '@/components/filters/types';
import { useAxios } from '@/hooks/useAxios';
import { useGetAccountAlerts } from '@/hooks/useGetAccountAlerts';
import { useMy } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useStableValue } from '@/hooks/useStableValue';
import { queryClient } from '@/queryclient';
import { useAssetApplicationParents } from '@/sections/asset/hooks/useAssetApplicationParents';
import { buildGraphQuery } from '@/sections/insights/queryBuilder/legacy/builders/graphQueryBuilder';
import {
  EntityBlock,
  LogicalOperator,
  QueryFilter,
  Relationship,
} from '@/sections/insights/queryBuilder/legacy/types/queryBuilder.types';
import { useAuth } from '@/state/auth';
import {
  Asset,
  AssetStatus,
  AssetWithWebpage,
  GraphQuery,
  NodeLabel,
  Operator,
  RelationshipNode,
  SeverityOpenCounts,
  TreeNode,
} from '@/types';
import { useMergeStatus, useMutation } from '@/utils/api';
import { buildAttackSurfaceFilter } from '@/utils/attackSurface.util';
import { updateEntityInQueryExecutorCache } from '@/utils/queryExecutorCache';
import { getResourceType } from '@/utils/resourceType.util';
import { sanitizeFilterValue } from '@/utils/shared/filterTransformations.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

/**
 * Variables for updating an asset
 *
 * NOTE: Comments are stored as History records in the backend.
 * When adding a comment without changing status, the current status
 * will be fetched automatically and sent with the comment to create
 * a history record.
 *
 * @property key - Asset key (required)
 * @property name - Asset name for display in toasts (required)
 * @property status - New status (optional) - when provided, creates status transition
 * @property comment - Comment text (optional) - requires status to create history record
 * @property tags - Asset tags (optional) - handled separately via dedicated operations
 * @property showSnackbar - Show toast notification (optional)
 */
interface UpdateAssetProps {
  key: string;
  name: string;
  status?: AssetStatus;
  comment?: string;
  tags?: string[] | null;
  showSnackbar?: boolean;
}

/**
 * Extract Asset entities from TreeNode query results.
 *
 * Dynamically collects all resource arrays from the result object (assets, awsresources,
 * gcpresources, azureresources, etc.) that contain asset-like objects with required fields.
 * Filters out non-asset specialized types (integrations, repositories, webapplications).
 */
export const combineAssetNodeResults = (treeNode: TreeNode): Asset[] => {
  const result = treeNode.node?.result as Record<string, unknown> | undefined;
  if (!result) return [];
  const assets: Asset[] = [];

  // Iterate over all keys in result object to dynamically collect resource arrays
  for (const key in result) {
    const value = result[key];

    // Skip non-array values and metadata fields
    if (!Array.isArray(value) || key === 'count') {
      continue;
    }

    // Collect all objects that have asset-like properties (key and identifier)
    // Filter out specialized types that shouldn't appear in asset relationships
    const resourceAssets = value.filter((item: unknown) => {
      if (!item || typeof item !== 'object') return false;

      // Type guard: after checking it's an object, cast to access properties
      const obj = item as Record<string, unknown>;

      // Must have key and identifier to be considered an asset
      if (!obj.key || !obj.identifier) return false;

      // Filter out specialized types by checking labels
      const labels = obj.labels || [];
      const isSpecializedType = (labels as string[]).some((label: string) =>
        ['Integration', 'Repository', 'WebApplication'].includes(label)
      );

      return !isSpecializedType;
    });

    assets.push(...resourceAssets);
  }

  return assets.filter(Boolean);
};

/**
 * Extract Asset entities from RelationshipNode query results.
 *
 * Dynamically collects all resource arrays from the result object (assets, awsresources,
 * gcpresources, azureresources, etc.) that contain asset-like objects with required fields.
 * Filters out non-asset specialized types (integrations, repositories, webapplications).
 */
export const combineAssetRelationshipResults = (
  relationship: RelationshipNode
): Asset[] => {
  const result = relationship.target?.result as
    | Record<string, unknown>
    | undefined;
  if (!result) return [];
  const assets: Asset[] = [];

  // Iterate over all keys in result object to dynamically collect resource arrays
  for (const key in result) {
    const value = result[key];

    // Skip non-array values and metadata fields
    if (!Array.isArray(value) || key === 'count') {
      continue;
    }

    // Collect all objects that have asset-like properties (key and identifier)
    // Filter out specialized types that shouldn't appear in asset relationships
    const resourceAssets = value.filter((item: unknown) => {
      if (!item || typeof item !== 'object') return false;

      // Type guard: after checking it's an object, cast to access properties
      const obj = item as Record<string, unknown>;

      // Must have key and identifier to be considered an asset
      if (!obj.key || !obj.identifier) return false;

      // Filter out specialized types by checking labels
      const labels = obj.labels || [];
      const isSpecializedType = (labels as string[]).some((label: string) =>
        ['Integration', 'Repository', 'WebApplication'].includes(label)
      );

      return !isSpecializedType;
    });

    assets.push(...resourceAssets);
  }

  return assets.filter(Boolean);
};

export const useUpdateAsset = () => {
  const axios = useAxios();

  const { invalidate: invalidateAlerts } = useGetAccountAlerts({
    enabled: false,
  });

  return useMutation<void, Error, UpdateAssetProps>({
    defaultErrorMessage: 'Failed to update asset',
    mutationFn: async ({ key, name, status, comment, showSnackbar = true }) => {
      // :Application keys are handled by /asset itself — the backend
      // detects #application# keys and cascades the same write to every
      // HAS_MEMBER child in one transaction. No special dispatch here.

      let promise;
      if (status === AssetStatus.Deleted) {
        promise = axios.delete('/asset', {
          data: {
            key,
          },
        });

        if (showSnackbar) {
          toast.promise(promise, {
            loading: `Deleting ${name}`,
            success: `Deleted ${name}`,
            error: `Failed to delete ${name}`,
          });
        }

        await promise;
      } else {
        // Build request body with only the fields that are provided
        const body: {
          key: string;
          type: string;
          name?: string;
          status?: string;
          comment?: string;
          tags?: string[] | null;
        } = {
          key,
          type: getResourceType(key), // Extract type from key format
        };

        // Add name if provided
        if (name !== undefined) body.name = name;

        // When adding a comment without changing status, we need to fetch current status
        // because backend requires BOTH status and comment to create history record
        if (comment !== undefined && status === undefined) {
          // Performance note: This code path should rarely execute if callers pass status from cached data
          console.warn(
            '[useUpdateAsset] Status not provided with comment, fetching from API (performance impact)'
          );

          try {
            // Fetch current asset to get its status
            const currentAsset = await axios.get(`/my`, {
              params: {
                label: 'asset',
                key: key,
                exact: true,
              },
            });
            const currentStatus = currentAsset.data.assets?.[0]?.status || 'A';
            body.status = currentStatus; // Include current status
            body.comment = comment;
          } catch (error) {
            // If status fetch fails, default to Active status
            // This allows comment to be added even if fetch fails
            console.warn(
              '[useUpdateAsset] Failed to fetch current status, defaulting to Active:',
              error
            );
            body.status = 'A';
            body.comment = comment;
          }
        } else {
          // Normal status update (with or without comment)
          if (status !== undefined) body.status = status;
          if (comment !== undefined) body.comment = comment;
        }
        // Tags are handled separately via dedicated tag operations
        // Removed: body.tags = null - This was causing 400 errors for comment operations

        promise = axios.put('/asset', body);

        if (showSnackbar) {
          toast.promise(promise, {
            loading: `Updating ${name}`,
            success: `Updated ${name}`,
            error: `Failed to update ${name}`,
          });
        }
      }

      await promise;
      invalidateAlerts();

      // Optimistic cache update for query-executor (instant UI feedback)
      const optimisticUpdate: { key: string; [k: string]: unknown } = { key };
      if (status !== undefined) optimisticUpdate.status = status;
      if (name !== undefined) optimisticUpdate.name = name;
      updateEntityInQueryExecutorCache(queryClient, 'assets', optimisticUpdate);

      // Invalidate all asset queries (table, drawer, filters)
      // Using same pattern as useAddComment and useDeleteAsset for consistency
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('asset'),
      });
    },
  });
};

export const useUpdateAssetMissionPrevalence = () => {
  const axios = useAxios();

  return useMutation<
    void,
    Error,
    { key: string; mission_prevalence: 'minimal' | 'support' | 'essential' }
  >({
    defaultErrorMessage: 'Failed to update mission prevalence',
    mutationFn: async ({ key, mission_prevalence }) => {
      await axios.put('/asset', {
        key,
        mission_prevalence,
      });
      // The backend recomputes affected risks' SSVC decisions when mission
      // prevalence changes, so both asset AND risk queries go stale. Awaited
      // broad-predicate invalidation, mirroring useUpdateRiskPublicWellBeing.
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return (
            queryKey[0] === 'MY' &&
            (queryKey[1] === 'asset' || queryKey[1] === 'risk')
          );
        },
        refetchType: 'active',
      });
    },
  });
};

export const useDeleteAsset = () => {
  const axios = useAxios();

  const { invalidate: invalidateAlerts } = useGetAccountAlerts({
    enabled: false,
  });

  return useMutation<void, Error, UpdateAssetProps>({
    defaultErrorMessage: 'Failed to delete asset',
    mutationFn: async ({ key, name }) => {
      const promise = axios.delete(`/asset`, {
        data: {
          key,
        },
      });

      toast.promise(promise, {
        loading: `Deleting ${name}`,
        success: `Deleted ${name}`,
        error: `Failed to delete ${name}`,
      });

      await promise;

      invalidateAlerts();
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('asset'),
      });
    },
  });
};

export const useHardDeleteAsset = () => {
  const axios = useAxios();

  const { invalidate: invalidateAlerts } = useGetAccountAlerts({
    enabled: false,
  });

  return useMutation<void, Error, UpdateAssetProps>({
    defaultErrorMessage: 'Failed to permanently delete asset',
    mutationFn: async ({ key, name }) => {
      const promise = axios.delete(`/asset/purge`, {
        data: {
          key,
        },
      });

      toast.promise(promise, {
        loading: `Permanently deleting ${name}`,
        success: `Permanently deleted ${name}`,
        error: `Failed to permanently delete ${name}`,
      });

      await promise;

      invalidateAlerts();
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('asset'),
      });
    },
  });
};

export const useCreateAsset = () => {
  const axios = useAxios();
  const { invalidate: invalidateJob } = useMy(
    { resource: 'job' },
    { enabled: false }
  );
  const { invalidate: invalidateProvidedAssets } = useMy({
    resource: 'attribute',
    query: '#surface#provided',
  });

  return useMutation<Asset, Error, Pick<Asset, 'identifier' | 'status'>>({
    defaultErrorMessage: `Failed to add asset`,
    getErrorFromResponse: true,
    mutationFn: async asset => {
      const promise = axios.put(`/asset`, {
        dns: asset.identifier,
        name: asset.identifier,
        status: asset.status || AssetStatus.Active,
        tags: null,
      });

      toast.promise(promise, {
        loading: 'Adding asset',
        success: `Added ${asset.identifier}`,
      });

      const { data } = await promise;

      invalidateJob();
      invalidateProvidedAssets();

      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('asset'),
      });

      return data;
    },
    onError: error => {
      toast.error('Failed to add asset', {
        description: `${error.name} ${error.message}`,
      });
    },
  });
};

export type PartialAsset = Pick<
  Asset,
  'visited' | 'key' | 'group' | 'identifier' | 'application' | 'class'
> & {
  riskSummary?: SeverityOpenCounts;
};

export interface Filters {
  search?: string;
  riskKey?: string;
  vulnerabilityId?: string;
  advancedFilters?: {
    // Asset Attribute filters
    new?: string[];
    port?: string[];
    protocol?: string[];
    tags?: string[];
    // Asset module filters
    asname?: string[];
    asnumber?: string[];
    status?: AssetStatus[];
    attackSurface?: string[];
    source?: string[];
    origins?: string[];
    // Scan profile filter
    profile?: string[];
    // Technology filters
    vendor?: string[];
    product?: string[];
    // Risk scoring filters
    riskSeverity?: string[];
    // Date range filter
    dateRange?: DateRangeFilter;
  };
  sort?: {
    orderBy: keyof Asset;
    descending: boolean;
  };
  negatedFilterKeys?: string[];
}

// Convert Asset Table filters to QueryBuilder EntityBlocks for buildGraphQuery
function convertFiltersToEntityBlocks(
  filters: Filters,
  excludeAccountSources: boolean
): EntityBlock[] {
  const blocks: EntityBlock[] = [];
  const negated = filters.negatedFilterKeys ?? [];

  // Create main Asset entity block
  const assetBlock: EntityBlock = {
    id: 'asset-block',
    entityType: 'Asset',
    filterGroups: [],
  };

  // Add status filters
  if (
    filters.advancedFilters?.status &&
    filters.advancedFilters.status.length > 0
  ) {
    const neg = negated.includes('status');
    assetBlock.filterGroups.push({
      id: 'status-group',
      logicalOperator: (neg ? 'AND' : 'OR') as LogicalOperator,
      filters: filters.advancedFilters.status.map((status, index) => ({
        id: `status-${index}`,
        attribute: 'status',
        operator: neg ? 'not equals' : 'equals',
        value: status,
      })),
    });
  }

  // Add other simple Asset filters
  // Exclude specific fields (status, vendor, product, source, new, protocol, port) from simpleFilters
  // These are handled separately via dedicated filter logic
  // Note: protocol and port require HAS_PORT relationship queries (PR #3413)
  const {
    status: _status, // eslint-disable-line @typescript-eslint/no-unused-vars
    vendor: _vendor, // eslint-disable-line @typescript-eslint/no-unused-vars
    product: _product, // eslint-disable-line @typescript-eslint/no-unused-vars
    source: _source, // eslint-disable-line @typescript-eslint/no-unused-vars
    new: _new, // eslint-disable-line @typescript-eslint/no-unused-vars
    protocol: _protocol, // eslint-disable-line @typescript-eslint/no-unused-vars
    port: _port, // eslint-disable-line @typescript-eslint/no-unused-vars
    dateRange: _dateRange, // eslint-disable-line @typescript-eslint/no-unused-vars
    attackSurface: _attackSurface, // eslint-disable-line @typescript-eslint/no-unused-vars
    profile: _profile, // eslint-disable-line @typescript-eslint/no-unused-vars
    ...simpleFilters
  } = filters.advancedFilters || {};

  // Handle 'new' filter specially - map to 'created' field with 'in the last' operator
  if (filters.advancedFilters?.new && filters.advancedFilters.new.length > 0) {
    assetBlock.filterGroups.push({
      id: 'created-group',
      logicalOperator: 'OR' as LogicalOperator,
      filters: filters.advancedFilters.new.map((val, index) => ({
        id: `created-${index}`,
        attribute: 'created',
        operator: 'in the last',
        value: val,
      })),
    });
  }

  // Handle 'dateRange' filter - convert to created/visited field with >= and <= operators
  if (filters.advancedFilters?.dateRange) {
    const { field, startDate, endDate } = filters.advancedFilters.dateRange;
    assetBlock.filterGroups.push({
      id: 'date-range-group',
      logicalOperator: 'AND' as LogicalOperator,
      filters: [
        {
          id: 'date-range-start',
          attribute: field,
          operator: 'greater than or equals',
          value: `${startDate}T00:00:00Z`,
        },
        {
          id: 'date-range-end',
          attribute: field,
          operator: 'less than or equals',
          value: `${endDate}T23:59:59Z`,
        },
      ],
    });
  }

  // Handle 'protocol' filter - requires HAS_PORT relationship (PR #3413)
  // Protocol/service names are stored in Port nodes via HAS_PORT relationship
  // processFilter will handle creating the HAS_PORT relationship with Port.service filter
  if (
    filters.advancedFilters?.protocol &&
    filters.advancedFilters.protocol.length > 0
  ) {
    const neg = negated.includes('protocol');
    assetBlock.filterGroups.push({
      id: 'protocol-group',
      logicalOperator: (neg ? 'AND' : 'OR') as LogicalOperator,
      filters: filters.advancedFilters.protocol.map((val, index) => ({
        id: `protocol-${index}`,
        attribute: 'protocol',
        operator: neg ? 'not equals' : 'equals',
        value: val,
      })),
    });
  }

  if (
    filters.advancedFilters?.port &&
    filters.advancedFilters.port.length > 0
  ) {
    const neg = negated.includes('port');
    assetBlock.filterGroups.push({
      id: 'port-group',
      logicalOperator: (neg ? 'AND' : 'OR') as LogicalOperator,
      filters: filters.advancedFilters.port.map((val, index) => ({
        id: `port-${index}`,
        attribute: 'port',
        operator: neg ? 'not equals' : 'equals',
        value: val,
      })),
    });
  }

  // Note: 'attackSurface' filter is handled separately by injecting boolean filters
  // directly into convertedQuery to avoid EntityBlock type constraints (ENG-1862)

  // Handle profile filter — empty string means IS NULL (no profile assigned)
  if (
    filters.advancedFilters?.profile &&
    filters.advancedFilters.profile.length > 0
  ) {
    const profileFilters = filters.advancedFilters.profile.map(
      (val: string, index: number) => ({
        id: `profile-${index}`,
        attribute: 'profile',
        operator: val === '' ? ('not exists' as const) : ('equals' as const),
        value: val === '' ? undefined : val,
      })
    );
    assetBlock.filterGroups.push({
      id: 'profile-group',
      logicalOperator: 'OR' as LogicalOperator,
      filters: profileFilters,
    });
  }

  Object.entries(simpleFilters).forEach(([key, value]) => {
    if (value && Array.isArray(value) && value.length > 0) {
      const neg = negated.includes(key);
      assetBlock.filterGroups.push({
        id: `${key}-group`,
        logicalOperator: (neg ? 'AND' : 'OR') as LogicalOperator,
        filters: value.map((val, index) => ({
          id: `${key}-${index}`,
          attribute: key,
          operator:
            key === 'tags'
              ? neg
                ? 'is none of'
                : 'is any of'
              : neg
                ? 'not equals'
                : 'equals',
          value: val,
        })),
      });
    }
  });

  // Add excludeAccountSources filter
  if (excludeAccountSources && !filters.riskKey) {
    assetBlock.filterGroups.push({
      id: 'exclude-account-group',
      logicalOperator: 'AND' as LogicalOperator,
      filters: [
        {
          id: 'exclude-account',
          attribute: 'source',
          operator: 'not equals',
          value: 'account',
        },
      ],
    });
  }

  blocks.push(assetBlock);

  return blocks;
}

export const defaultFilters: Filters = {
  advancedFilters: {},
  sort: {
    orderBy: 'visited',
    descending: true,
  },
};

export function useQueryAssets({
  enabled = true,
  limit = 100,
  parentFilters,
  excludeAccountSources = false,
}: {
  enabled?: boolean;
  limit?: number;
  parentFilters?: Filters;
  excludeAccountSources?: boolean;
} = {}) {
  // When parentFilters exist (drawer context), use ephemeral state so drawer
  // filters never leak to the main table. Every drawer open starts fresh.
  const isDrawerContext = !!parentFilters;

  const { isPraetorianUser } = useAuth();

  const [persistedFilters, setPersistedFilters] = useStorage<Filters>(
    {
      queryKey: 'assetFiltersV3',
      key: StorageKey.FILTERS_ASSETS_V3,
      ephemeralKeys: ['search'],
    },
    defaultFilters
  );

  // Sanitize stale status filters: non-Praetorian users should never see the
  // Deleted ('D') status. If a customer previously had 'D' in their persisted
  // filters (e.g., from a Praetorian impersonation session) strip it out so it
  // does not silently ghost their results.
  const sanitizedPersistedFilters: Filters = useMemo(() => {
    if (isPraetorianUser) return persistedFilters;
    const status = persistedFilters.advancedFilters?.status;
    if (!status || !status.includes(AssetStatus.Deleted))
      return persistedFilters;
    return {
      ...persistedFilters,
      advancedFilters: {
        ...persistedFilters.advancedFilters,
        status: status.filter(s => s !== AssetStatus.Deleted),
      },
    };
  }, [isPraetorianUser, persistedFilters]);

  const [ephemeralFilters, setEphemeralFilters] =
    useState<Filters>(defaultFilters);

  const localFilters = isDrawerContext
    ? ephemeralFilters
    : sanitizedPersistedFilters;
  const setFilters = isDrawerContext
    ? setEphemeralFilters
    : setPersistedFilters;

  const filters: Filters = parentFilters
    ? { ...parentFilters, ...localFilters }
    : localFilters;
  // BUGFIX: Use 0ms delay when clearing search, 300ms when typing
  // This prevents the "empty table" bug when clearing a search that returned no results
  const [debouncedSearch] = useDebounce(
    filters.search,
    filters.search === '' ? 0 : 300
  );

  const stableAdvancedFilters = useStableValue(filters.advancedFilters);

  // The customer-facing asset table always queries :Asset (observation-shaped
  // rows). The :CanonicalAsset label was retired backend-side in #5816/#5911,
  // so a query for it returns 0 rows. :Application is intentionally NOT an
  // :Asset (application.go GetLabels = [Application, Group, TTL]) so it never
  // appears in this result set — the parent Application is surfaced per-row via
  // useAssetApplicationParents (a chip annotation).
  const tableLabel: NodeLabel = 'Asset';

  const { convertedQuery, isFiltersApplied, searchParam } = useMemo(() => {
    // Build search param for fulltext index (assetSearchIndex covers key, name, dns, identifier)
    const searchParam =
      debouncedSearch && debouncedSearch.length >= 2
        ? debouncedSearch
        : undefined;

    // Convert Asset Table filters to QueryBuilder EntityBlocks
    const entityBlocks = convertFiltersToEntityBlocks(
      filters,
      excludeAccountSources
    );

    // Use QueryBuilder's proven buildGraphQuery logic
    const graphQuery = buildGraphQuery(
      entityBlocks,
      [], // No inter-group operators for simple case
      {
        // Asset-specific options
        ...(stableAdvancedFilters?.vendor && {
          technologyVendorValues: stableAdvancedFilters.vendor,
        }),
        ...(stableAdvancedFilters?.product && {
          technologyProductValues: stableAdvancedFilters.product,
        }),
        ...(stableAdvancedFilters?.source && {
          sourceValues: stableAdvancedFilters.source,
        }),
      },
      undefined, // vulnFilterOptions
      undefined, // technologyFilterOptions
      undefined, // seedFilterOptions
      undefined, // integrationValues
      undefined, // queryContentTab
      filters.riskKey // Pass riskKey to buildGraphQuery
    );

    const hasFilters = entityBlocks.some(
      block =>
        block.filterGroups.length > 0 ||
        (stableAdvancedFilters &&
          Object.keys(stableAdvancedFilters).length > 0) ||
        !!debouncedSearch ||
        excludeAccountSources ||
        !!filters.riskKey ||
        !!filters.vulnerabilityId
    );

    // Build attack surface boolean filters (ENG-1862)
    // Bypasses EntityBlock to use proper boolean values for Neo4j RANGE indexes
    const surfaceFilterNode = filters.advancedFilters?.attackSurface?.length
      ? (buildAttackSurfaceFilter(
          filters.advancedFilters.attackSurface
        ) as QueryFilter)
      : null;

    // Convert QueryBuilder's GraphQuery to useMy-compatible format
    const convertedQuery = graphQuery
      ? {
          ...graphQuery,
          node: {
            ...graphQuery.node,
            // The table always queries :Asset; the parent Application is
            // surfaced via a per-row chip (useAssetApplicationParents).
            labels: [tableLabel],
            // Fulltext search — covers key, name, dns, identifier via assetSearchIndex
            ...(searchParam ? { search: searchParam } : {}),
            filters: [
              ...(graphQuery.node.filters?.map((filter: QueryFilter) => ({
                ...filter,
                operator: filter.operator as Operator,
              })) ?? []),
              ...(surfaceFilterNode ? [surfaceFilterNode] : []),
            ],
            relationships: [
              ...(graphQuery.node.relationships?.map((rel: Relationship) => ({
                ...rel,
                target: rel.target
                  ? {
                      ...rel.target,
                      labels: rel.target.labels as NodeLabel[],
                      filters: rel.target.filters?.map(
                        (filter: QueryFilter) => ({
                          ...filter,
                          operator: filter.operator as Operator,
                        })
                      ),
                    }
                  : undefined,
              })) ?? []),
              // Nested-hop match for vulnerabilityId: Asset -> HAS_WEBPAGE|HAS_PORT
              // -> () -> HAS_VULNERABILITY -> Risk(name = <vulnerability id>).
              // Mirrors the frozen legacy builder's riskKey block
              // (queryBuilder/legacy/builders/graphQueryBuilder.ts) but filters on
              // Risk.name, which equals Vulnerability.id at every minting site.
              ...(filters.vulnerabilityId
                ? [
                    {
                      label: ['HAS_WEBPAGE', 'HAS_PORT'],
                      target: {
                        labels: [] as NodeLabel[],
                        filters: [],
                        relationships: [
                          {
                            label: 'HAS_VULNERABILITY',
                            target: {
                              labels: ['Risk'] as NodeLabel[],
                              filters: [
                                {
                                  field: 'name',
                                  operator: '=' as Operator,
                                  value: filters.vulnerabilityId,
                                },
                              ],
                            },
                          },
                        ],
                      },
                    },
                  ]
                : []),
            ],
          },
        }
      : null;

    return {
      convertedQuery,
      isFiltersApplied: hasFilters,
      searchParam,
    };
  }, [
    filters,
    stableAdvancedFilters,
    debouncedSearch,
    excludeAccountSources,
    tableLabel,
  ]);

  // Only use tree query when port/protocol filters are active (requires HAS_PORT relationships).
  // tree: true triggers ScanTree on the backend which runs expensive Neo4j path queries.
  // For the default view (no port/protocol filters), Scan is sufficient and much faster.
  const needsTreeQuery = Boolean(
    filters.advancedFilters?.port?.length ||
      filters.advancedFilters?.protocol?.length
  );

  const backendSortKey = filters.sort
    ? (SORT_KEY_TO_BACKEND[filters.sort.orderBy] ?? filters.sort.orderBy)
    : undefined;

  const query = useMy(
    {
      resource: 'asset',
      tree: needsTreeQuery || undefined,
      query:
        convertedQuery && isFiltersApplied
          ? ({
              // Construct proper GraphQuery matching Asset Table interface
              node: {
                ...convertedQuery.node,
                filters: convertedQuery.node.filters?.map(
                  (filter: QueryFilter) => ({
                    ...filter,
                    value: sanitizeFilterValue(filter.value) || filter.value,
                  })
                ),
              },
              limit: limit,
              page: 0,
              ...(filters.sort && filters.sort.orderBy === 'status'
                ? {
                    orderByFields: [
                      {
                        field: 'visited',
                        descending: true,
                      },
                    ],
                  }
                : filters.sort && backendSortKey
                  ? {
                      orderBy: backendSortKey,
                      descending: filters.sort.descending,
                    }
                  : {}),
            } as GraphQuery)
          : ({
              node: {
                labels: [tableLabel],
                ...(searchParam ? { search: searchParam } : {}),
              },
              limit: limit,
              page: 0,
              ...(filters.sort && filters.sort.orderBy === 'status'
                ? {
                    orderByFields: [
                      {
                        field: 'visited',
                        descending: true,
                      },
                    ],
                  }
                : filters.sort && backendSortKey
                  ? {
                      orderBy: backendSortKey,
                      descending: filters.sort.descending,
                    }
                  : {}),
            } as GraphQuery),
    },
    {
      enabled,
      // Keep previous data visible during search to prevent blanking on every keystroke
      // Search is excluded from filterKey, so table doesn't remount
      // Filter/sort changes still remount via filterKey for scroll reset
      placeholderData: keepPreviousData,
      staleTime: 30_000,
      // Abort superseded fetches on unmount/remount (threat-drawer CVE flips,
      // search churn) instead of stacking uncancelled Neo4j round trips; safe
      // here because nothing does wait-and-reapply against the assets list.
      abortOnUnmount: true,
    }
  );

  const assets = useMemo(() => {
    if (!query.data?.length) return [];

    if (needsTreeQuery) {
      // Tree format: extract assets from tree nodes and merge relationship data
      const result = query.data
        .map((tree): AssetWithWebpage | null => {
          const entities = combineAssetNodeResults(tree);

          // Extract webpage from relationships (legacy pattern)
          const webpage =
            tree?.node?.relationships?.[0]?.target?.result?.webpages?.[0];

          // Extract ports from HAS_PORT relationships (PR #3413)
          // When filtering by port/protocol, the Port nodes are returned in relationships
          const ports = tree?.node?.relationships
            ?.filter((rel: RelationshipNode) => rel.label === 'HAS_PORT')
            .flatMap((rel: RelationshipNode) => rel.target?.result?.ports || [])
            .filter(Boolean);

          // Safe access to first entity
          const firstEntity = entities[0];
          if (!firstEntity) {
            return null; // Return null instead of empty object
          }

          // Merge Asset with extracted ports
          return {
            ...firstEntity,
            webpage,
            // Include ports from relationships (takes precedence over Asset's ports field)
            ...(ports && ports.length > 0 && { ports }),
          } as AssetWithWebpage;
        })
        .filter((asset): asset is AssetWithWebpage => asset !== null);

      // BUGFIX: Deduplicate assets by key to prevent duplicate rows in table
      // This handles cases where graph queries with OPTIONAL relationships return
      // the same asset multiple times (once per matched relationship).
      // Example: Asset with 2 HAS_PORT relationships returns 2 rows from Neo4j.
      // Related: CHARIOT-1567
      const seenKeys = new Set<string>();
      return result.filter(asset => {
        if (seenKeys.has(asset.key)) {
          return false; // Skip duplicate
        }
        seenKeys.add(asset.key);
        return true; // Keep first occurrence
      });
    }

    // Flat format: data is already Asset objects from handleGraphQueryResponse in useMy
    return query.data as AssetWithWebpage[];
  }, [query.data, needsTreeQuery]);

  // Assets associated to a specific risk (riskKey, matching Risk.key) or to a
  // vulnerability (vulnerabilityId, matching Risk.name — equal to the CVE id at
  // every backend minting site). The above query gets assets via the nested
  // webpage/port hop; the below query gets assets directly linked to the risk.
  // BUGFIX: Apply user-selected filters (status, search, tags, attackSurface) to this query as well
  const riskAssetQuery = useMy(
    {
      resource: 'asset',
      query: {
        node: {
          labels: ['Asset'] as NodeLabel[],
          // Fulltext search — same node-root search as the main query; without
          // it, directly-linked assets ignore the search box (search is not a
          // member of node.filters, so copying filters alone misses it).
          ...(searchParam ? { search: searchParam } : {}),
          // Apply same user filters as main query (with sanitization), fallback to exclude deleted assets
          filters: convertedQuery?.node?.filters
            ? convertedQuery.node.filters.map((filter: QueryFilter) => ({
                ...filter,
                value: sanitizeFilterValue(filter.value) || filter.value,
              }))
            : [
                {
                  field: 'status',
                  operator: '=' as Operator,
                  value: AssetStatus.Deleted,
                  not: true,
                },
              ],
          relationships: [
            {
              label: 'HAS_VULNERABILITY' as const,
              target: {
                labels: ['Risk'] as NodeLabel[],
                filters: [
                  filters.riskKey
                    ? {
                        field: 'key',
                        operator: '=' as Operator,
                        value: filters.riskKey,
                      }
                    : {
                        field: 'name',
                        operator: '=' as Operator,
                        value: filters.vulnerabilityId || '',
                      },
                ],
              },
            },
          ],
        },
      } as GraphQuery,
    },
    {
      enabled: enabled && Boolean(filters.riskKey || filters.vulnerabilityId),
      placeholderData: keepPreviousData,
      staleTime: 30_000,
      // Abort superseded fetches on unmount/remount (threat-drawer CVE flips,
      // search churn) instead of stacking uncancelled Neo4j round trips; safe
      // here because nothing does wait-and-reapply against the assets list.
      abortOnUnmount: true,
    }
  );

  // :Application rows can't appear here — they don't carry the :Asset label
  // (post-#5911), so the :Asset query excludes them. No post-filter needed.
  // Dedupe by asset.key across the two queries — an asset linked to the risk
  // both directly and via a webpage/port would otherwise render twice.
  const combinedData = useMemo(() => {
    const seenKeys = new Set<string>();
    return [...assets, ...riskAssetQuery.data].filter(asset => {
      if (seenKeys.has(asset.key)) return false;
      seenKeys.add(asset.key);
      return true;
    });
  }, [assets, riskAssetQuery.data]);

  // Resolve each member observation's parent :Application via HAS_MEMBER
  // and graft it onto the row as `asset.application = { key, fqdn }`.
  // The FQDN is read from the matched parent Application node, never
  // from `member.group` — that field is reroute-fragile.
  // See rollup-frontend-plan.md item A.
  const pageMemberKeys = useMemo(
    () => combinedData.map(asset => asset.key).filter(Boolean),
    [combinedData]
  );
  const { parentsByAssetKey } = useAssetApplicationParents(pageMemberKeys);

  const data = useMemo(() => {
    if (parentsByAssetKey.size === 0) return combinedData;
    return combinedData.map(asset => {
      const parent = parentsByAssetKey.get(asset.key);
      if (!parent) return asset;
      // Application rows are their own parent — don't self-link.
      if (parent.key === asset.key) return asset;
      return { ...asset, application: parent } as AssetWithWebpage;
    });
  }, [combinedData, parentsByAssetKey]);
  const status = useMergeStatus(
    query.status,
    filters.riskKey || filters.vulnerabilityId
      ? riskAssetQuery.status
      : 'success'
  );

  return {
    data,
    error: query.error || riskAssetQuery.error,
    status,
    isLoading: query.isLoading,
    isFetching: query.isFetching || riskAssetQuery.isFetching,
    // True when either leg is serving ANOTHER query key's rows rather than this
    // key's own data. Both legs set `placeholderData: keepPreviousData`, so on a
    // key change with a cold cache the observer returns the previous key's `data`
    // at `status: 'success'` — callers that make a claim about the current key
    // MUST consult this, not `status` alone.
    //
    // The risk-scoped leg is guarded exactly as `status` is above: it is only
    // `enabled` when a riskKey is present, and a disabled leg's placeholder-ness
    // is not meaningful to callers. Guarding is the fail-safe here — an unguarded
    // OR could only ever report MORE placeholder-ness, which would suppress
    // content for the riskKey-less consumers (the assets table).
    isPlaceholderData:
      query.isPlaceholderData ||
      (filters.riskKey ? riskAssetQuery.isPlaceholderData : false),
    fetchNextPage: () => {
      query.fetchNextPage();
      if (filters.riskKey || filters.vulnerabilityId) {
        riskAssetQuery.fetchNextPage();
      }
    },
    isFetchingNextPage:
      query.isFetchingNextPage || riskAssetQuery.isFetchingNextPage,
    hasNextPage: query.hasNextPage || riskAssetQuery.hasNextPage,
    refetch: () => {
      query.refetch();
      if (filters.riskKey || filters.vulnerabilityId) {
        riskAssetQuery.refetch();
      }
    },
    invalidate: () => {
      query.invalidate();
      if (filters.riskKey || filters.vulnerabilityId) {
        riskAssetQuery.invalidate();
      }
    },
    isFilteredEmpty: status !== 'pending' && data.length === 0,
    filters,
    setFilters,
    isFiltersApplied,
    convertedQuery, // QueryBuilder's graph query converted to useMy format
    activeQuery: convertedQuery, // Expose as activeQuery for export modal
  };
}
